import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Users, Shield, Eye, Settings, Trash2, Pencil, Copy, Check, Link2, KeyRound, Ban, CheckCircle2 } from 'lucide-react';

const FONT = { heading: "'Manrope', sans-serif", body: "'IBM Plex Sans', sans-serif", data: "'JetBrains Mono', monospace" };

const PERM_LEVELS = [
  { value: 'none', label: 'No Access', color: 'text-slate-400', bg: '' },
  { value: 'view', label: 'View Only', color: 'text-blue-600', bg: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'manage', label: 'View + Manage', color: 'text-amber-600', bg: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'full', label: 'Full Access', color: 'text-emerald-600', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

const getPermBadge = (level) => PERM_LEVELS.find(p => p.value === level) || PERM_LEVELS[0];

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [copiedWebhook, setCopiedWebhook] = useState('');

  const [formData, setFormData] = useState({
    email: '', password: '', full_name: '', role: 'manager', department: '', region: '', form_permissions: {}
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, formsRes] = await Promise.all([
        api.get('/users'),
        api.get('/forms-all')
      ]);
      setUsers(usersRes.data);
      setForms(formsRes.data);
    } catch (err) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditingUser(null);
    setFormData({ email: '', password: '', full_name: '', role: 'manager', department: '', region: '', form_permissions: {} });
    setDialogOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      full_name: user.full_name,
      role: user.role,
      department: user.department || '',
      region: user.region || '',
      form_permissions: user.form_permissions || {}
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        const updateData = { ...formData };
        delete updateData.email;
        if (!updateData.password) delete updateData.password;
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success('User updated');
      } else {
        if (!formData.email || !formData.password || !formData.full_name) {
          toast.error('Please fill required fields');
          return;
        }
        await api.post('/auth/register', formData);
        toast.success('User created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const toggleStatus = async (u) => {
    if (u.id === currentUser?.id) { toast.error("You can't deactivate your own account"); return; }
    const next = u.status === 'active' ? 'inactive' : 'active';
    const msg = next === 'inactive'
      ? `Deactivate ${u.full_name}? They will be signed out immediately and blocked from logging in.`
      : `Reactivate ${u.full_name}? They will be able to log in again.`;
    if (!window.confirm(msg)) return;
    try {
      await api.put(`/users/${u.id}`, { status: next });
      toast.success(next === 'inactive' ? 'User deactivated' : 'User reactivated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };


  const setPermission = (formId, level) => {
    setFormData(prev => {
      const perms = { ...prev.form_permissions };
      if (level === 'none') {
        delete perms[formId];
      } else {
        perms[formId] = level;
      }
      return { ...prev, form_permissions: perms };
    });
  };

  const setAllPermissions = (level) => {
    setFormData(prev => {
      const perms = {};
      if (level !== 'none') {
        forms.forEach(f => { perms[f.id] = level; });
      }
      return { ...prev, form_permissions: perms };
    });
  };

  const copyWebhook = (url) => {
    navigator.clipboard.writeText(window.location.origin + url);
    setCopiedWebhook(url);
    setTimeout(() => setCopiedWebhook(''), 2000);
  };

  // Count permissions per user
  const getUserPermSummary = (user) => {
    const perms = user.form_permissions || {};
    const count = Object.keys(perms).length;
    if (user.role === 'admin') return { text: 'All Forms', badge: 'bg-emerald-50 text-emerald-700' };
    if (count === 0) return { text: 'No Forms', badge: 'bg-red-50 text-red-600' };
    return { text: `${count} Form${count > 1 ? 's' : ''}`, badge: 'bg-blue-50 text-blue-700' };
  };

  return (
    <div className="space-y-6" style={{ fontFamily: FONT.body }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: FONT.heading }}>User Management</h1>
          <p className="text-xs text-slate-400 mt-1">Manage users, roles, and form-level access permissions</p>
        </div>
        {currentUser?.role === 'admin' && (
          <Button onClick={openCreate} className="rounded-sm" data-testid="add-user-btn"><UserPlus className="w-4 h-4 mr-2" />Add User</Button>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">User</TableHead>
              <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Role</TableHead>
              <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Form Access</TableHead>
              <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
              <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map(u => {
              const permSummary = getUserPermSummary(u);
              return (
                <TableRow key={u.id} className="border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{u.full_name}</p>
                      <p className="text-[10px] text-slate-400" style={{ fontFamily: FONT.data }}>{u.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] rounded-sm capitalize">{u.role === 'course_qa' ? 'Course QA Reviewer' : u.role}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm border ${permSummary.badge}`}>{permSummary.text}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={`text-[10px] rounded-sm ${u.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{u.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {currentUser?.role === 'admin' && (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => toggleStatus(u)} disabled={u.id === currentUser?.id}
                          className="h-8 w-8 p-0 rounded-sm" data-testid={`toggle-status-${u.id}`}
                          title={u.id === currentUser?.id ? "You can't deactivate yourself" : (u.status === 'active' ? 'Deactivate login' : 'Reactivate login')}>
                          {u.status === 'active'
                            ? <Ban className="w-3.5 h-3.5 text-red-500" />
                            : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="h-8 w-8 p-0 rounded-sm" data-testid={`edit-user-${u.id}`}>
                          <Pencil className="w-3.5 h-3.5 text-slate-500" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {users.length === 0 && !loading && (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">No users found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: FONT.heading }}>
              {editingUser ? <Pencil className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
              {editingUser ? 'Edit User' : 'Create New User'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Full Name *</Label>
                <Input value={formData.full_name} onChange={e => setFormData(p => ({ ...p, full_name: e.target.value }))} className="mt-1 rounded-sm" data-testid="user-name" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Email *</Label>
                <Input value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} disabled={!!editingUser} className="mt-1 rounded-sm" data-testid="user-email" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">{editingUser ? 'New Password (leave blank to keep)' : 'Password *'}</Label>
                <Input type="password" value={formData.password} onChange={e => setFormData(p => ({ ...p, password: e.target.value }))} className="mt-1 rounded-sm" data-testid="user-password" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Role</Label>
                <Select value={formData.role} onValueChange={v => setFormData(p => ({ ...p, role: v }))}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="sales_rep">Sales Rep</SelectItem>
                    <SelectItem value="course_qa">Course QA Reviewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Form Permissions */}
            <div className="border border-slate-200 rounded-sm overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-slate-500" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600" style={{ fontFamily: FONT.heading }}>Form Permissions & Webhook Access</h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400">Set all:</span>
                  {PERM_LEVELS.map(p => (
                    <button key={p.value} onClick={() => setAllPermissions(p.value)}
                      className={`text-[9px] font-semibold px-2 py-1 rounded-sm border transition-colors hover:opacity-80 ${p.bg || 'bg-white border-slate-200 text-slate-400'}`}>
                      {p.value === 'none' ? 'None' : p.label.split(' ')[0]}
                    </button>
                  ))}
                </div>
              </div>

              {formData.role === 'admin' ? (
                <div className="p-4 text-center text-xs text-emerald-600 bg-emerald-50/50">
                  <Shield className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
                  Admin users automatically have full access to all forms and webhooks
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {forms.map(form => {
                    const currentPerm = formData.form_permissions[form.id] || 'none';
                    const permInfo = getPermBadge(currentPerm);
                    const hasAccess = currentPerm !== 'none';
                    return (
                      <div key={form.id} className="px-4 py-3 flex items-center gap-4" data-testid={`form-perm-${form.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{form.name}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <Badge variant="outline" className="text-[9px] rounded-sm">{form.form_type}</Badge>
                            {hasAccess && form.webhook_url && (
                              <button onClick={() => copyWebhook(form.webhook_url)} className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600 transition-colors" data-testid={`copy-webhook-${form.id}`}>
                                {copiedWebhook === form.webhook_url ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                                <span style={{ fontFamily: FONT.data }}>{form.webhook_url}</span>
                              </button>
                            )}
                          </div>
                        </div>
                        <Select value={currentPerm} onValueChange={v => setPermission(form.id, v)}>
                          <SelectTrigger className={`w-[150px] h-8 rounded-sm text-xs ${permInfo.bg || 'border-slate-200'}`} data-testid={`perm-select-${form.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PERM_LEVELS.map(p => (
                              <SelectItem key={p.value} value={p.value} className="text-xs">
                                <span className={`font-medium ${p.color}`}>{p.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                  {forms.length === 0 && (
                    <div className="p-6 text-center text-xs text-slate-400">No forms available. Create a form first.</div>
                  )}
                </div>
              )}
            </div>

            {/* Permission Legend */}
            <div className="flex items-center gap-4 text-[10px] text-slate-500 px-1">
              <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> <b>View:</b> Dashboard & data</span>
              <span className="flex items-center gap-1"><Settings className="w-3 h-3" /> <b>Manage:</b> + Webhook & mapping</span>
              <span className="flex items-center gap-1"><KeyRound className="w-3 h-3" /> <b>Full:</b> + Delete & configure</span>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSubmit} className="rounded-sm" data-testid="save-user-btn">{editingUser ? 'Update User' : 'Create User'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
